import React from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  MessageCircle, MapPin, Star, Phone, MessageSquare, Mail, Wrench,
  Wallet, ShieldCheck, Sparkles, ArrowRight, Plus, Tag, CalendarDays, Wifi, Coffee, Award,
} from "lucide-react";
import {
  LOCATIONS, MESSENGER_URL, CONTACT, ADMIN_CONTACTS, CARETAKER_CONTACTS,
  RATE_TIERS, RATE_ADD_ONS, LONG_STAY_DISCOUNTS,
} from "@/data/staycation";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ n = 5 }) {
  return (
    <div className="inline-flex items-center gap-0.5 text-[var(--xs-primary)]">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`h-3.5 w-3.5 ${i < n ? "fill-current" : "opacity-30"}`} />
      ))}
    </div>
  );
}

function SectionHeader({ eyebrow, title, subtitle, href, hrefLabel }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
      <div>
        <p className="xs-eyebrow">{eyebrow}</p>
        <h2 className="font-display text-3xl sm:text-4xl font-black mt-2 leading-[1.1]">{title}</h2>
        {subtitle && <p className="mt-2 text-neutral-600 text-sm max-w-xl">{subtitle}</p>}
      </div>
      {href && (
        <Link to={href} className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--xs-primary)] hover:underline whitespace-nowrap" data-testid={`landing-link-${eyebrow.toLowerCase().replace(/\s+/g, '-')}`}>
          {hrefLabel} <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

const FALLBACK_IMG = "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1200&q=80&auto=format&fit=crop";
const onImgErr = (e) => { if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG; };
const avatarUrl = (name) => `https://ui-avatars.com/api/?name=${encodeURIComponent((name || "Guest").trim())}&size=80&background=FF385C&color=fff&bold=true&format=png`;

export default function Landing() {
  const [units, setUnits] = React.useState([]);
  const [reviews, setReviews] = React.useState([]);

  React.useEffect(() => {
    Promise.all([
      axios.get(`${API}/units`).then((r) => setUnits(r.data || [])).catch(() => {}),
      axios.get(`${API}/reviews`).then((r) => setReviews(r.data || [])).catch(() => {}),
    ]);
  }, []);

  const featuredUnits = React.useMemo(() => {
    // One unit per location for visual variety
    const byLoc = new Map();
    units.forEach((u) => { if (!byLoc.has(u.location_key)) byLoc.set(u.location_key, u); });
    return Array.from(byLoc.values()).slice(0, 4);
  }, [units]);

  const featuredReviews = React.useMemo(() => {
    return reviews.filter((r) => (r.comment || "").length > 80 && (r.comment || "").length < 240).slice(0, 6);
  }, [reviews]);

  const counts = React.useMemo(() => {
    const map = {};
    units.forEach((u) => { map[u.location_key] = (map[u.location_key] || 0) + 1; });
    return map;
  }, [units]);

  return (
    <div data-testid="landing-page" className="bg-white">
      <Seo
        title="Welcome to Xanders Staycations — everything you need to know"
        description="Your complete guide to 30 cozy staycation units at Shell, Shore 1, Shore 2 and South Residences. See rates, locations, real photos, 333 verified guest reviews, and direct contact numbers — book on Messenger."
        path="/landing"
      />

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-gradient-to-br from-[var(--xs-primary-soft)] via-white to-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 pt-16 pb-14 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-neutral-200 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-[var(--xs-primary)]">
              <Sparkles className="h-3 w-3" /> Welcome to Xanders Staycations
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-4 leading-[1.02]">
              Everything you need to know <span className="text-[var(--xs-primary)]">before booking</span>.
            </h1>
            <p className="mt-5 text-neutral-700 text-base sm:text-lg max-w-2xl">
              30 cozy staycation units. 4 prime locations near SM Mall of Asia & SM Southmall. Hosted by Airbnb Superhost Alexander. Below is your one-page summary — locations, rates, real photos, verified reviews and direct contacts.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href={MESSENGER_URL} target="_blank" rel="noopener noreferrer" data-testid="landing-hero-messenger"
                 className="inline-flex items-center gap-2 rounded-full bg-[var(--xs-primary)] hover:bg-[var(--xs-primary-hover)] text-white px-6 py-3 text-sm font-semibold transition-colors">
                <MessageCircle className="h-4 w-4" /> Book via Messenger
              </a>
              <Link to="/units" data-testid="landing-hero-units"
                    className="inline-flex items-center gap-2 rounded-full border border-neutral-300 hover:border-[var(--xs-primary)] hover:text-[var(--xs-primary)] px-6 py-3 text-sm font-semibold transition-colors">
                Browse all 30 units <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              <div className="rounded-xl bg-white border border-neutral-200 p-3 text-center">
                <p className="font-display text-2xl font-black text-[var(--xs-primary)]">30</p>
                <p className="text-[11px] text-neutral-600 uppercase tracking-wider">Units</p>
              </div>
              <div className="rounded-xl bg-white border border-neutral-200 p-3 text-center">
                <p className="font-display text-2xl font-black text-[var(--xs-primary)]">333</p>
                <p className="text-[11px] text-neutral-600 uppercase tracking-wider">5★ Reviews</p>
              </div>
              <div className="rounded-xl bg-white border border-neutral-200 p-3 text-center">
                <p className="font-display text-2xl font-black text-[var(--xs-primary)]">₱1,750</p>
                <p className="text-[11px] text-neutral-600 uppercase tracking-wider">Starting</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-neutral-100 aspect-[4/5]">
              <img
                src="https://customer-assets.emergentagent.com/job_affordable-staycay/artifacts/fv9ylmo3_FB%20PAGE.jpg"
                alt="Xanders Staycations cover"
                onError={onImgErr}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-black/70 to-transparent">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-[11px] font-semibold text-neutral-800">
                  <Award className="h-3 w-3 text-[var(--xs-primary)]" /> Airbnb Superhost • Alexander
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US strip */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="landing-why">
        {[
          { Icon: MapPin, t: "Prime locations", d: "Walking distance to SM MOA & SM Southmall." },
          { Icon: Wallet, t: "Honest rates", d: "From ₱1,750/night. No hidden fees, no security deposit." },
          { Icon: ShieldCheck, t: "Superhost trust", d: "333 verified 5-star reviews from real guests." },
          { Icon: MessageCircle, t: "Quick replies", d: "Chat us on Messenger — confirmed in minutes." },
        ].map(({ Icon, t, d }) => (
          <div key={t} className="xs-card p-5">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[var(--xs-primary-soft)]">
              <Icon className="h-5 w-5 text-[var(--xs-primary)]" />
            </div>
            <h3 className="mt-3 font-display font-bold text-base">{t}</h3>
            <p className="mt-1 text-xs text-neutral-600">{d}</p>
          </div>
        ))}
      </section>

      {/* LOCATIONS */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16" data-testid="landing-locations">
        <SectionHeader
          eyebrow="Our 4 Locations"
          title="Pick where you'd love to stay"
          subtitle="All units are inside secure SMDC condominium complexes with pools, gyms and 24/7 security."
          href="/units"
          hrefLabel="See all units"
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LOCATIONS.map((loc) => (
            <Link key={loc.key} to={`/units?location=${loc.key}`} data-testid={`landing-location-${loc.key}`}
                  className="xs-card overflow-hidden group">
              <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                <img src={loc.image} alt={loc.name} onError={onImgErr} loading="eager"
                     className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-[var(--xs-primary)]">{loc.area}</p>
                <h3 className="font-display font-bold text-lg mt-1">{loc.name}</h3>
                <p className="text-xs text-neutral-600 mt-1">{counts[loc.key] || 0} units · {loc.near}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED UNITS */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16 bg-neutral-50 rounded-3xl" data-testid="landing-units">
        <SectionHeader
          eyebrow="Featured Units"
          title="A taste of every location"
          subtitle="One handpicked unit from each tower so you can see the range."
          href="/units"
          hrefLabel="Browse all 30 units"
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {featuredUnits.map((u) => (
            <article key={u.id} className="xs-card overflow-hidden" data-testid={`landing-featured-unit-${u.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-neutral-100 relative">
                <img src={u.image} alt={u.name} onError={onImgErr} loading="eager"
                     className="w-full h-full object-cover" />
                <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold">
                  <MapPin className="h-3 w-3 text-[var(--xs-primary)]" /> {u.near}
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-display font-bold text-base leading-tight">{u.name}</h3>
                <p className="text-xs text-neutral-600 mt-0.5">{u.type} · up to {u.guests} guests</p>
                <div className="mt-3 flex items-center justify-between">
                  <p className="font-display font-black text-lg text-[var(--xs-primary)]">₱{u.rate_php?.toLocaleString()}<span className="text-xs text-neutral-500 font-semibold">/night</span></p>
                  <a href={`${MESSENGER_URL}?ref=unit_${u.id}`} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--xs-primary)] hover:underline">
                    Book <ArrowRight className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* RATES */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16" data-testid="landing-rates">
        <SectionHeader
          eyebrow="Our Rates"
          title="Transparent pricing"
          subtitle="No hidden fees, no security deposit. What you see is what you pay."
          href="/rates"
          hrefLabel="Full rates page"
        />
        <div className="grid md:grid-cols-3 gap-5">
          {RATE_TIERS.slice(0, 3).map((tier, idx) => (
            <div key={tier.name} className="xs-card p-6" data-testid={`landing-rate-${idx}`}>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-[var(--xs-primary-soft)] text-[var(--xs-primary)] px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider">
                <Tag className="h-3 w-3" /> {tier.badge}
              </div>
              <h3 className="font-display font-bold text-xl mt-3">{tier.name}</h3>
              <p className="font-display font-black text-3xl mt-2 text-[var(--xs-primary)]">
                ₱{tier.nightly?.toLocaleString()}<span className="text-sm text-neutral-500 font-semibold">/night</span>
              </p>
              <p className="text-xs text-neutral-600 mt-2">{tier.description}</p>
              <ul className="mt-4 space-y-1.5 text-xs text-neutral-700">
                {(tier.includes || []).slice(0, 4).map((i) => (
                  <li key={i} className="flex items-start gap-2"><Plus className="h-3 w-3 mt-0.5 text-[var(--xs-primary)] shrink-0" />{i}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="xs-card p-5">
            <h4 className="font-display font-bold text-sm flex items-center gap-2"><Plus className="h-4 w-4 text-[var(--xs-primary)]" /> Optional add-ons</h4>
            <ul className="mt-3 space-y-1.5 text-xs text-neutral-700">
              {RATE_ADD_ONS.slice(0, 5).map((a) => (
                <li key={a.name} className="flex items-center justify-between gap-3">
                  <span>{a.name}</span>
                  <span className="font-semibold text-neutral-900 whitespace-nowrap">{a.price}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="xs-card p-5">
            <h4 className="font-display font-bold text-sm flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[var(--xs-primary)]" /> Long-stay discounts</h4>
            <ul className="mt-3 space-y-1.5 text-xs text-neutral-700">
              {LONG_STAY_DISCOUNTS.map((d) => (
                <li key={d.label} className="flex items-center justify-between gap-3">
                  <span><span className="font-semibold">{d.label}</span> <span className="text-neutral-500">· {d.range}</span></span>
                  <span className="font-semibold text-[var(--xs-primary)] whitespace-nowrap">−{d.percent}%</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16 bg-neutral-50 rounded-3xl" data-testid="landing-reviews">
        <SectionHeader
          eyebrow="Guest Reviews"
          title="333 verified 5-star reviews"
          subtitle="Real stories from Airbnb-verified bookings. Here's a small sample."
          href="/reviews"
          hrefLabel="Read all reviews"
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featuredReviews.map((r) => (
            <article key={r.id} className="xs-card p-5" data-testid={`landing-review-${r.id}`}>
              <Stars n={r.rating || 5} />
              <p className="mt-3 text-sm text-neutral-800 leading-relaxed line-clamp-5">“{r.comment}”</p>
              <div className="mt-4 flex items-center gap-3 pt-3 border-t border-neutral-100">
                <img src={avatarUrl(r.name)} alt={r.name} width="36" height="36" loading="lazy"
                     onError={(e) => { e.currentTarget.style.display = "none"; }}
                     className="h-9 w-9 rounded-full object-cover border border-neutral-100" />
                <div>
                  <p className="text-sm font-semibold leading-tight">{r.name}</p>
                  <p className="text-[11px] text-neutral-500">{r.location} · {r.date}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CONTACTS */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16" data-testid="landing-contacts">
        <SectionHeader
          eyebrow="How to Reach Us"
          title="Direct contacts, no middleman"
          subtitle="Messenger is fastest. Call/text any admin for booking, or a caretaker for on-site concerns during your stay."
          href="/contact"
          hrefLabel="Contact page"
        />
        <div className="grid lg:grid-cols-3 gap-5">
          <a href={MESSENGER_URL} target="_blank" rel="noopener noreferrer" data-testid="landing-contact-messenger"
             className="rounded-[1.25rem] p-6 bg-[var(--xs-primary)] text-white hover:bg-[var(--xs-primary-hover)] transition-colors">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/20">
              <MessageCircle className="h-5 w-5" />
            </div>
            <h3 className="font-display text-lg font-bold mt-4">Chat on Messenger</h3>
            <p className="mt-2 text-sm text-white/90">Fastest way to confirm availability and book.</p>
            <p className="mt-3 text-sm font-semibold">m.me/558751367325531 →</p>
          </a>

          <div className="xs-card p-5" data-testid="landing-admin-contacts">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--xs-primary-soft)]">
              <Phone className="h-5 w-5 text-[var(--xs-primary)]" />
            </div>
            <h3 className="font-display font-bold text-lg mt-3">Admins — call or text</h3>
            <ul className="mt-3 space-y-2.5">
              {ADMIN_CONTACTS.map((c) => {
                const tel = c.phone.replace(/\s/g, "");
                return (
                  <li key={c.name} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{c.name}</p>
                      <p className="text-xs text-neutral-600 truncate">{c.phone}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <a href={`tel:${tel}`} aria-label={`Call ${c.name}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 hover:border-[var(--xs-primary)] hover:text-[var(--xs-primary)]">
                        <Phone className="h-3.5 w-3.5" />
                      </a>
                      <a href={`sms:${tel}`} aria-label={`Text ${c.name}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 hover:border-[var(--xs-primary)] hover:text-[var(--xs-primary)]">
                        <MessageSquare className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="xs-card p-5" data-testid="landing-caretaker-contacts">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--xs-primary-soft)]">
              <Wrench className="h-5 w-5 text-[var(--xs-primary)]" />
            </div>
            <h3 className="font-display font-bold text-lg mt-3">Caretakers — on the ground</h3>
            <ul className="mt-3 space-y-2.5">
              {CARETAKER_CONTACTS.map((c) => {
                const tel = c.phone.replace(/\s/g, "");
                return (
                  <li key={c.name} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{c.name}</p>
                      <p className="text-[11px] text-[var(--xs-primary)] font-semibold truncate">{c.area}</p>
                      <p className="text-xs text-neutral-600 truncate">{c.phone}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <a href={`tel:${tel}`} aria-label={`Call ${c.name}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 hover:border-[var(--xs-primary)] hover:text-[var(--xs-primary)]">
                        <Phone className="h-3.5 w-3.5" />
                      </a>
                      <a href={`sms:${tel}`} aria-label={`Text ${c.name}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 hover:border-[var(--xs-primary)] hover:text-[var(--xs-primary)]">
                        <MessageSquare className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div className="xs-card p-5">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[var(--xs-primary-soft)]">
              <Mail className="h-4 w-4 text-[var(--xs-primary)]" />
            </div>
            <h4 className="font-display font-bold mt-3">Email</h4>
            <a href={`mailto:${CONTACT.email}`} className="text-xs text-neutral-700 hover:text-[var(--xs-primary)] break-all">{CONTACT.email}</a>
          </div>
          <div className="xs-card p-5">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[var(--xs-primary-soft)]">
              <MapPin className="h-4 w-4 text-[var(--xs-primary)]" />
            </div>
            <h4 className="font-display font-bold mt-3">Location</h4>
            <p className="text-xs text-neutral-700">{CONTACT.address}</p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-24">
        <div className="rounded-3xl bg-gradient-to-br from-[var(--xs-primary)] to-[var(--xs-primary-hover)] text-white p-10 sm:p-14 text-center">
          <Sparkles className="h-6 w-6 mx-auto opacity-90" />
          <h3 className="font-display text-3xl sm:text-4xl font-black mt-3">Ready when you are.</h3>
          <p className="mt-3 max-w-xl mx-auto text-white/90 text-sm sm:text-base">Tap below and tell us your dates — we'll handle the rest, just like we've done for 333+ happy guests.</p>
          <a href={MESSENGER_URL} target="_blank" rel="noopener noreferrer" data-testid="landing-cta-messenger"
             className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-[var(--xs-primary)] hover:bg-white/90 px-6 py-3 text-sm font-semibold transition-colors">
            <MessageCircle className="h-4 w-4" /> Book via Messenger
          </a>
        </div>
      </section>
    </div>
  );
}
